import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';

import { TRUST_PILOT_LINK } from '@common/constants';
import { cx } from '@emotion/css';
import styled from '@emotion/styled';

interface IReviewProps {
  styles: any;
  name: string;
  title: string;
  details: string;
}
const CarouselLayoutContainer = styled.div`
  //// slick-track is the container of all slide items
  .slick-track {
    display: flex;
  }
  // slick slide is the container of a single slide item. There is also a wrapper div before our custom component
  .slick-slide {
    display: flex;
    align-self: stretch;
    height: unset;
    > div {
      display: flex;
      align-self: stretch;
      width: 100%;
    }
  }
`;

const Review = (props: IReviewProps) => {
  const { styles, name, title, details } = props;
  return (
    <div className={cx(styles.review)}>
      <div className={styles.rating_sec}>
        <Image
          src="/images/icons/google-star-reviews.jpg"
          width={104}
          height={16}
          alt="star rate"
          className={{ ...styles.img }}
        />
        <p className={styles.p}>{name}</p>
      </div>
      <div className={styles.review_cntnt}>
        <h4 className={cx(styles.h4, styles.review_head)}>{title}</h4>
        <p className={cx(styles.p, styles.review_desc)}>{details}</p>
      </div>
    </div>
  );
};

// NOTE: react-slick's `responsive` prop is unreliable for the *initial* value.
// It registers one matchMedia listener per band in componentDidMount and only
// updates state when a listener's query happens to match at that moment; there
// is no ordering guarantee across bands (including its own unowned "wider than
// the largest breakpoint" catch-all query), so depending on the device either a
// real band or that catch-all can end up deciding the initial render — and
// which one wins isn't something we control from this component. That showed up
// as phones and 32" monitors alternately rendering whatever the top-level base
// `slidesToShow` happened to be, regardless of the actual viewport.
//
// So instead of `responsive`, we compute slidesToShow ourselves from the real
// window width — synchronously on mount and again on every resize — and pass it
// straight into <Slider>. Ordered narrowest-first so we can use the first match.
const BREAKPOINTS: Array<{ maxWidth: number; slidesToShow: number }> = [
  { maxWidth: 767, slidesToShow: 1 },
  { maxWidth: 992, slidesToShow: 2 },
  { maxWidth: 1450, slidesToShow: 3 },
  { maxWidth: Infinity, slidesToShow: 4 },
];

const getSlidesToShow = (width: number): number =>
  BREAKPOINTS.find((bp) => width <= bp.maxWidth)!.slidesToShow;

const useSlidesToShow = (): number => {
  // Default to the mobile value so SSR/first paint never over-renders slides
  // before we can measure the real viewport on the client.
  const [slidesToShow, setSlidesToShow] = useState<number>(1);

  useEffect(() => {
    const updateSlidesToShow = () => {
      setSlidesToShow(getSlidesToShow(window.innerWidth));
    };
    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);

  return slidesToShow;
};

const baseSettings = {
  slidesToScroll: 1,
  arrows: true,
  dots: false,
  centerMode: false,
  swipeToSlide: true,
  touchMove: true,
  // prevArrow: <PrevArrow />,
  // nextArrow: <NextArrow />,
};

const TrustPilot = ({ styles }: any) => {
  const slidesToShow = useSlidesToShow();
  const settings = { ...baseSettings, slidesToShow };

  function NextArrow(props: any) {
    const { onClick } = props;
    return (
      <button
        className={cx(styles.slick_next, styles.cstm_slick_arrow)}
        style={{}}
        aria-label="Next button to goto the next review"
        onClick={onClick}>
        <Image
          src="/images/icons/arrow-black.svg"
          width={17}
          height={15}
          alt="next button"
        />
      </button>
    );
  }

  function PrevArrow(props: any) {
    const { onClick } = props;
    return (
      <button
        className={cx(styles.slick_prev, styles.cstm_slick_arrow)}
        style={{}}
        aria-label="Back button to goto the previous review"
        onClick={onClick}>
        <Image
          src="/images/icons/arrow-black.svg"
          alt="back button"
          width={17}
          height={15}
        />
      </button>
    );
  }

  return (
    <section className={cx(styles.sec, styles.trustpilot_sec, styles.root)}>
      <div className={styles.container}>
        <div className={styles.row}>
          <div
            className={cx(styles.heading_blk, styles.text_center, styles.w100)}>
            <h2 className={cx(styles.h2, styles.title)}>Our Reviews</h2>
            <Link
              legacyBehavior
              href={TRUST_PILOT_LINK}
              className={styles.link}
              target="_blank">
              <a className={styles.link} target="_blank">
                View{' '}
                <span className={styles.link_arrow}>
                  <Image
                    src="/images/icons/arrow-red.svg"
                    alt="trust pilot link arrow icon"
                    width={15}
                    height={12}
                  />
                </span>
              </a>
            </Link>
          </div>
        </div>
      </div>
      <CarouselLayoutContainer
        className={cx(styles.review_slider_wrpr, styles.w100)}>
        <Slider
          {...settings}
          className={cx(styles.review_slider)}
          nextArrow={<NextArrow />}
          prevArrow={<PrevArrow />}>
          <Review
            styles={styles}
            name="Panos"
            title="Amazing experience"
            details="Amazing experience with Qeeper! Their customer service is super quick, phone, email, everything. They work extended hours. I used them for an EPC, but they do much more. Highly recommend!"
          />
          <Review
            styles={styles}
            name="Elaine"
            title="I had a great experience with this company!"
            details="I had a great experience with this company! They fixed my windows very well, and the quality of the work is excellent. The technicians were friendly, polite, and worked quickly without any fuss. Everything was done efficiently and professionally."
          />
          <Review
            styles={styles}
            name="Angus"
            title="An excellent service"
            details="I used this company to inspect a number of gas appliances at my property. The engineer arrived on time and dealt with the appliances quickly and efficiently. An excellent service, and in future I would use the company again."
          />
          <Review
            styles={styles}
            name="Delphine"
            title="On time & good explaination"
            details="A satisfying experience with Qeeper, in terms of coordination, explaination, maintenance of the extractor fans and respect of time slots."
          />

          <Review
            styles={styles}
            name="Connie"
            title="Happy with Qeeper service!"
            details="Qeeper provides prompt reply and explanations to the chartered EPC service. Customer service Angela is attending the needs of customers with relevant advice. Happy with Qeeper service!"
          />
        </Slider>
      </CarouselLayoutContainer>
    </section>
  );
};

export default TrustPilot;
